import React from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'white' | 'gray';
  text?: string;
  fullScreen?: boolean;
  overlay?: boolean;
  className?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  text,
  fullScreen = false,
  overlay = false,
  className = ''
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  // Color classes
  const colorClasses = {
    primary: 'text-primary-600',
    secondary: 'text-secondary-600',
    white: 'text-white',
    gray: 'text-secondary-400'
  };

  // Text size classes
  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const spinnerClasses = `
    animate-spin
    ${sizeClasses[size]}
    ${colorClasses[color]}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const spinner = (
    <svg className={spinnerClasses} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const content = (
    <div className="flex flex-col items-center">
      {spinner}
      {text && (
        <p className={`mt-2 ${textSizeClasses[size]} ${colorClasses[color]} font-medium`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90">
        {content}
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75 rounded">
        {content}
      </div>
    );
  }

  return content;
};

// Preset spinner variants
export const InlineSpinner: React.FC<Omit<LoadingSpinnerProps, 'fullScreen' | 'overlay'>> = (props) => (
  <LoadingSpinner {...props} />
);

export const OverlaySpinner: React.FC<Omit<LoadingSpinnerProps, 'fullScreen' | 'overlay'>> = (props) => (
  <LoadingSpinner overlay {...props} />
);

export const FullScreenSpinner: React.FC<Omit<LoadingSpinnerProps, 'fullScreen' | 'overlay'>> = (props) => (
  <LoadingSpinner fullScreen {...props} />
);

// Loading dots animation
interface LoadingDotsProps {
  color?: 'primary' | 'secondary' | 'white' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LoadingDots: React.FC<LoadingDotsProps> = ({
  color = 'primary',
  size = 'md',
  className = ''
}) => {
  const colorClasses = {
    primary: 'bg-primary-600',
    secondary: 'bg-secondary-600',
    white: 'bg-white',
    gray: 'bg-secondary-400'
  };

  const sizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-2 h-2',
    lg: 'w-3 h-3'
  };

  const dotClasses = `
    rounded-full
    ${sizeClasses[size]}
    ${colorClasses[color]}
    animate-pulse
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={`flex space-x-1 ${className}`}>
      <div className={dotClasses} style={{ animationDelay: '0ms' }}></div>
      <div className={dotClasses} style={{ animationDelay: '150ms' }}></div>
      <div className={dotClasses} style={{ animationDelay: '300ms' }}></div>
    </div>
  );
};

// Loading bar (progress-like)
interface LoadingBarProps {
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  height?: 'thin' | 'normal' | 'thick';
  className?: string;
}

export const LoadingBar: React.FC<LoadingBarProps> = ({
  color = 'primary',
  height = 'normal',
  className = ''
}) => {
  const colorClasses = {
    primary: 'bg-primary-600',
    secondary: 'bg-secondary-600',
    success: 'bg-success-600',
    warning: 'bg-warning-600',
    danger: 'bg-danger-600'
  };

  const heightClasses = {
    thin: 'h-1',
    normal: 'h-2',
    thick: 'h-3'
  };

  return (
    <div className={`w-full bg-secondary-200 rounded-full overflow-hidden ${className}`}>
      <div
        className={`
          ${heightClasses[height]}
          ${colorClasses[color]}
          animate-pulse
          rounded-full
          transition-all duration-300
        `}
        style={{
          width: '100%',
          animation: 'loading-slide 1.5s ease-in-out infinite'
        }}
      />
      <style>{`
        @keyframes loading-slide {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

// Pulse loading (for content placeholders)
interface PulseLoaderProps {
  lines?: number;
  width?: string[];
  height?: number;
  className?: string;
  animated?: boolean;
}

export const PulseLoader: React.FC<PulseLoaderProps> = ({
  lines = 3,
  width = ['100%', '75%', '50%'],
  height = 16,
  className = '',
  animated = true
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className={`bg-secondary-200 rounded ${animated ? 'animate-pulse' : ''}`}
          style={{
            width: width[index] || width[0] || '100%',
            height: `${height}px`
          }}
        />
      ))}
    </div>
  );
};

// Loading state wrapper component
interface LoadingWrapperProps {
  loading: boolean;
  spinner?: React.ReactNode;
  overlay?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const LoadingWrapper: React.FC<LoadingWrapperProps> = ({
  loading,
  spinner,
  overlay = true,
  className = '',
  children
}) => {
  return (
    <div className={`relative ${className}`}>
      {children}
      {loading && (
        <>
          {spinner || <OverlaySpinner text="กำลังโหลด..." />}
        </>
      )}
    </div>
  );
};

export default LoadingSpinner;